import { identity, map, omit, pickBy } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subscription, catchError, finalize, of, take } from 'rxjs';
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { StorageHelper, ValidationHelper } from '@diktup/frontend/helpers';

import { AppService, AuthService, SeoService, SharedService } from '../../services';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class BaseResetPasswordComponent implements OnInit {
  private subscr: Subscription;

  seo: any;
  current = '/';
  token: string;
  showAlert = false;
  resultUrl: string;
  showMessageSuccess = false;
  resetPasswordForm: FormGroup;
  isResetButtonDisabled: boolean;

  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected seoService: SeoService,
    protected appService: AppService,
    protected titleTagService: Title,
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected storageHelper: StorageHelper,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected activatedRoute: ActivatedRoute,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
      });
    this.activatedRoute.params.subscribe((params) => {
      if (params['token']) {
        this.token = params['token']?.split('&')[0];
        this.subscr = this.router.events.subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.current = event.url;
          } else if (event instanceof NavigationEnd) {
            this.current = event.url;
            const pattern = /^\/v\//;
            const match = this.current.match(pattern);
            this.resultUrl = match ? match[0] : '';
            if (this.resultUrl === '/v/') {
              this.appService
                .validateEmailForTarget(this.token)
                .pipe(
                  catchError(() => {
                    this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
                      this.toastrService.error(text);
                    });
                    this.changeDetectorRef.markForCheck();
                    return of(null);
                  }),
                )
                .subscribe((res) => {
                  if (res) {
                    this.translate.get('SHARED.EMAIL_VERIFIED').subscribe((text: string) => {
                      this.toastrService.success(text);
                    });
                    this.router.navigateByUrl('/');
                    changeDetectorRef.markForCheck();
                  }
                });
            }
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.resetPasswordForm = this.formBuilder.group({
      pwd: this.formBuilder.group(
        {
          password: ['', Validators.required],
          confirmPassword: ['', Validators.required],
        },
        { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
      ),
    });
  }

  resetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      return;
    }
    this.resetPasswordForm.disable();
    this.showAlert = false;
    const formValue = {
      ...omit(this.resetPasswordForm.value, 'pwd'),
      password: this.resetPasswordForm.value.pwd.password,
    };
    this.authService
      .resetPasswordForTarget(this.storageHelper.getData('posId'), formValue.password, this.token)
      .pipe(
        finalize(() => {
          this.resetPasswordForm.enable();
          this.resetPasswordForm.reset();
          this.showAlert = true;
        }),
        catchError(() => {
          this.toastrService.error("Quelque chose s'est mal passé");
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.isResetButtonDisabled = true;
          this.toastrService.success('Votre mot de passe a été réinitialisé.');
          this.router.navigateByUrl('/');
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.subscr.unsubscribe();
  }
}
